import axios from 'axios';
import qs from 'query-string';
import { compose, forEach, fromPairs, isEmpty, join, map, prop, toPairs } from 'ramda';
import EventSourcePolyfill from 'eventsource';

/* ---- Config ---- */

let globalConfig = { debug: true };
const endPoint = (path, config = globalConfig) => `${config.endpoint}${path}`;
const serverEndPoint = (path, config = globalConfig) => `${config.serverEndpoint}${path}`;
const config = config => (globalConfig = { ...globalConfig, ...config }); // eslint-disable-line no-shadow
const SCOPES = ['AUTHENTICATED', 'APPROVED'];

const buildAuthHeaders = ({ accessToken, jwt }) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${accessToken}`,
  ...(jwt && { 'Authorization-Person': jwt }),
});

const createSearchParams = ({ plateNumber, vin, phoneNumber }) => {
  const params = new URLSearchParams();
  if (phoneNumber) {
    params.append('phone_number', phoneNumber);
  }
  if (vin) {
    params.append('vin', vin);
  }
  if (plateNumber) {
    params.append('plate_number', plateNumber);
  }

  return params.toString();
};

/* ---- Authentication and Captcha ---- */

const authenticate = () => {
  const params = {
    grant_type: 'client_credentials',
    client_id: globalConfig.clientId,
    client_secret: globalConfig.clientSecret,
    scope: join(' ', SCOPES),
  };

  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
  const query = qs.stringify(params);
  return axios.post(serverEndPoint('/oauth2/token'), query, { headers }).then(prop('data'));
};

const requestOTP = ({ auth: { accessToken, captchaJwt }, version, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    ...(captchaJwt && { 'Captcha-Jwt': captchaJwt }),
  };
  return axios.post(endPoint(`/public/${version}/otp`), data, { headers }).then(prop('data'));
};

const getJWT = ({ auth: { accessToken, captchaJwt }, version, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    ...(captchaJwt && { 'Captcha-Jwt': captchaJwt }),
  };
  return axios.post(endPoint(`/public/${version}/token`), data, { headers }).then(prop('data'));
};

const getCaptchaJWT = ({ auth, version, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.post(endPoint(`/public/${version}/captcha_token`), data, { headers }).then(prop('data'));
};

const getTrustedJWT = ({ auth, version, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.post(endPoint(`/public/${version}/trusted_token`), data, { headers }).then(prop('data'));
};

const requestIdentification = ({ auth: { accessToken, captchaJwt }, version, data }) => {
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
    ...(captchaJwt && { 'Captcha-Jwt': captchaJwt }),
  };
  return axios.post(endPoint(`/public/${version}/token/regenerate`), data, { headers }).then(prop('data'));
};

/* ---- Product ---- */

const getProductConfiguration = ({ auth, version, productCode }) => {
  const headers = buildAuthHeaders(auth);
  return axios.get(endPoint(`/public/${version}/products/${productCode}`), { headers }).then(prop('data'));
};

/* ---- Claim, Location, Highway, Eligibility, Immobilization ---- */

const getClaimOperation = ({ auth, version, claimId }) => {
  const headers = buildAuthHeaders(auth);
  return axios.get(endPoint(`/public/${version}/claims/${claimId}/operation`), { headers }).then(prop('data'));
};

const createClaim = ({ auth, version, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.post(endPoint(`/public/${version}/claims`), data, { headers }).then(prop('data'));
};

const putClaimEvent = ({ auth, version, claimId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.put(endPoint(`/public/${version}/claims/${claimId}/event`), data, { headers }).then(prop('data'));
};

const putClaimLocation = ({ auth, version, claimId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.put(endPoint(`/public/${version}/claims/${claimId}/location`), data, { headers }).then(prop('data'));
};

const getEventAdditionalQuestions = ({ auth, version, claimId, eventCode }) => {
  const headers = buildAuthHeaders(auth);
  return axios.get(endPoint(`/public/${version}/claims/${claimId}/event_questions?eventCode=${eventCode}`), { headers }).then(prop('data'));
};

const searchClaim = ({ auth, version, data: { plateNumber, vin, phoneNumber } }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .get(endPoint(`/public/${version}/claims/search?${createSearchParams({ plateNumber, vin, phoneNumber })}`), {
      headers,
    })
    .then(prop('data'));
};

const searchClaimById = ({ auth, version, data: { id } }) => {
  const headers = buildAuthHeaders(auth);
  return axios.get(endPoint(`/public/${version}/claims/${id}`), { headers }).then(prop('data'));
};

const getClaimHistory = ({ auth, version, claimId }) => {
  const headers = buildAuthHeaders(auth);
  return axios.get(endPoint(`/public/${version}/claims/${claimId}/history`), { headers }).then(prop('data'));
};

const checkCscEligibility = ({ auth, version, claimId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.post(endPoint(`/public/${version}/claims/${claimId}/check_csc_eligibility`), data, { headers }).then(prop('data'));
};

const getCscSettings = ({ auth, version, claimId, missionId }) => {
  const headers = buildAuthHeaders(auth);
  return axios.get(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/csc_settings`), { headers }).then(prop('data'));
};

const updateImmobilizationGarage = ({ auth, version, claimId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.post(endPoint(`/public/${version}/claims/${claimId}/update_immobilisation_garage`), data, { headers }).then(prop('data'));
};

const npsEligibilityClaim = ({ auth, version, claimId }) => {
  const headers = buildAuthHeaders(auth);
  return axios.get(endPoint(`/public/${version}/claims/${claimId}/nps_eligibility`), { headers }).then(prop('data'));
};

const putNpsEligibilityClaim = ({ auth, version, claimId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.put(endPoint(`/public/${version}/claims/${claimId}/nps`), data, { headers }).then(prop('data'));
};

const getBrandSuggestions = ({ auth, version, hint }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .get(endPoint(`/public/${version}/vehicles/makes_suggestion?make_hint=${hint}&max_suggestions=${globalConfig.maxBrandSuggestions}`), {
      headers,
    })
    .then(prop('data'));
};

const getModelSuggestions = ({ auth, version, brand }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .get(endPoint(`/public/${version}/vehicles/models_suggestion?make_hint=${brand}&max_suggestions=${globalConfig.maxModelSuggestions}`), {
      headers,
    })
    .then(prop('data'));
};

const getHighwaySections = ({ auth, version, lat, lng, metres }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .get(endPoint(`/public/${version}/localisation/highway_sections/search?latitude=${lat}&longitude=${lng}&metres=${metres}`), { headers })
    .then(prop('data'));
};

const fetchHighways = ({ auth, version, lat, lng }) => {
  const headers = buildAuthHeaders(auth);
  return axios.get(endPoint(`/public/${version}/localisation/highways?latitude=${lat}&longitude=${lng}`), { headers }).then(prop('data'));
};

const fetchHighwayDirections = ({ auth, version, lat, lng, highwayCode }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .get(endPoint(`/public/${version}/localisation/highways/${highwayCode}/directions?latitude=${lat}&longitude=${lng}`), { headers })
    .then(prop('data'));
};

const fetchHighwayPOIs = ({ auth, version, lat, lng, highwayCode, directionCode }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .get(
      endPoint(
        `/public/${version}/localisation/highways/${highwayCode}/directions/${directionCode}/` +
          `points-of-interest?latitude=${lat}&longitude=${lng}`
      ),
      { headers }
    )
    .then(prop('data'));
};

const searchVehicleInfo = ({ auth, version, plateNumber, vin, isVinMandatory }) => {
  const headers = buildAuthHeaders(auth);
  let params = `plate_number=${plateNumber}`;
  if (vin) {
    params += `&vin=${vin}`;
  }
  if (isVinMandatory) {
    params += `&isVinMandatory=${isVinMandatory}`;
  }
  return axios.get(endPoint(`/public/${version}/vehicles/search?${params}`), { headers }).then(prop('data'));
};

const putClaimVehicleLoss = ({ auth, version, claimId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.put(endPoint(`/public/${version}/claims/${claimId}/vehicle_loss`), data, { headers }).then(prop('data'));
};

const getInsuranceCompanies = ({ auth, version, name }) => {
  const headers = buildAuthHeaders(auth);
  return axios.get(endPoint(`/public/${version}/insurance_companies/suggestion?name=${name}`), { headers }).then(prop('data'));
};

/* ---- Digital Channel ---- */

const digitalInteractions = ({ auth, version, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.post(endPoint(`/public/${version}/interactions`), data, { headers }).then(prop('data'));
};

const createClaimDigitalExit = ({ auth, version, claimId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.put(endPoint(`/public/${version}/claims/${claimId}/digital_exit`), data, { headers }).then(prop('data'));
};

const createMissionDigitalExit = ({ auth, version, claimId, missionId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .put(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/digital_exit`), data, { headers })
    .then(prop('data'));
};

/* ---- Mission (1st and common) ---- */

const createMission = ({ auth, version, claimId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.post(endPoint(`/public/${version}/claims/${claimId}/missions`), data, { headers }).then(prop('data'));
};

const transferToOem = ({ auth, version, claimId }) => {
  const headers = buildAuthHeaders(auth);
  return axios.post(endPoint(`/public/${version}/claims/${claimId}/transfer_to_oem`), {}, { headers }).then(prop('data'));
};

const createOemMission = ({ auth, version, claimId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.post(endPoint(`/public/${version}/claims/${claimId}/missions/oem_mission`), data, { headers }).then(prop('data'));
};

const createOemRentalMission = ({ auth, version, claimId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.post(endPoint(`/public/${version}/claims/${claimId}/missions/oem_rental_mission`), data, { headers }).then(prop('data'));
};

const getAllMissions = ({ auth, version, claimId }) => {
  const headers = buildAuthHeaders(auth);
  return axios.get(endPoint(`/public/${version}/claims/${claimId}/missions`), { headers }).then(prop('data'));
};

const cancelMission = ({ auth, version, claimId, missionId }) => {
  const headers = buildAuthHeaders(auth);
  return axios.delete(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}`), { headers }).then(prop('data'));
};

const cancelMissionCost = ({ auth, version, claimId, missionId }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .get(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/cancel_mission_cost`), { headers })
    .then(prop('data'));
};

const automaticCancelMission = ({ auth, version, claimId, missionId }) => {
  const headers = buildAuthHeaders(auth);
  const data = {
    reasonCode: 'CUSTOMER_REQUESTED_CANCELLATION',
  };
  const url = endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/cancel_mission`);
  return axios.post(url, data, { headers }).then(prop('data'));
};

const putVehicleDestination = ({ auth, version, missionId, claimId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .put(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/vehicle_destination`), data, { headers })
    .then(prop('data'));
};

const getCarDestinations = ({ auth, version, missionId, claimId }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .get(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/vehicle_destinations/search`), {
      headers,
    })
    .then(prop('data'))
    .catch(error => {
      if (error.response && error.response.status === 404 && error.response.data && isEmpty(error.response.data)) {
        return [];
      }
      throw error;
    });
};

const getSpecificCarDestinations = ({ auth, version, claimId, missionId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .post(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/vehicle_destinations/search_specific_location`), data, {
      headers,
    })
    .then(prop('data'))
    .catch(error => {
      if (error.response && error.response.status === 404 && error.response.data && isEmpty(error.response.data)) {
        return [];
      }
      throw error;
    });
};

const getTowLimits = ({ auth, version, claimId, missionId }) => {
  const headers = buildAuthHeaders(auth);
  return axios.get(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/tow_limits`), { headers }).then(prop('data'));
};

const getRosLimits = ({ auth, version, claimId, missionId }) => {
  const headers = buildAuthHeaders(auth);
  return axios.get(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/ros_limits`), { headers }).then(prop('data'));
};

const dispatchMission = ({ auth, version, claimId, missionId }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .post(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/dispatch`), undefined, { headers })
    .then(prop('data'));
};

const updateMission = ({ auth, version, claimId, missionId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.put(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}`), data, { headers }).then(prop('data'));
};

const updateMissionType = ({ auth, version, claimId, missionId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .put(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/mission_type`), data, { headers })
    .then(prop('data'));
};

const startMissionMonitoring = ({ version, claimId, onMessage, onError, auth: { accessToken, jwt } }) => {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };
  const eventSource = new EventSourcePolyfill(endPoint(`/public/${version}/claims/${claimId}/missions/events?jwt=${jwt}`), {
    headers,
  });
  eventSource.onerror = onError;
  eventSource.onmessage = ({ data }) => onMessage(JSON.parse(data));
  return eventSource;
};

/* ---- Rental ---- */

const createRentalMission = ({ auth, version, claimId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios.post(endPoint(`/public/${version}/claims/${claimId}/missions/rental_mission`), data, { headers }).then(prop('data'));
};

const updateRentalMission = ({ auth, version, claimId, missionId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .put(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/rental_mission`), data, { headers })
    .then(prop('data'));
};

const extendRentalMission = ({ auth, version, claimId, missionId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .post(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/extend_rental`), data, { headers })
    .then(prop('data'));
};

const rentalCurrentLimits = ({ auth, version, claimId, missionId }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .get(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/rental_current_limits`), { headers })
    .then(prop('data'));
};

const rentalExtensionInfo = ({ auth, version, claimId, missionId, data }) => {
  const headers = buildAuthHeaders(auth);
  return axios
    .put(endPoint(`/public/${version}/claims/${claimId}/missions/${missionId}/rental_extension_info`), data, {
      headers,
    })
    .then(prop('data'));
};

const methods = {
  config,
  authenticate,
  requestOTP,
  getJWT,
  getCaptchaJWT,
  getTrustedJWT,
  requestIdentification,
  getProductConfiguration,
  getClaimOperation,
  createClaim,
  putClaimEvent,
  searchClaim,
  searchClaimById,
  getClaimHistory,
  putClaimLocation,
  getEventAdditionalQuestions,
  checkCscEligibility,
  updateImmobilizationGarage,
  npsEligibilityClaim,
  putNpsEligibilityClaim,
  getBrandSuggestions,
  getModelSuggestions,
  getHighwaySections,
  fetchHighways,
  fetchHighwayDirections,
  fetchHighwayPOIs,
  searchVehicleInfo,
  putClaimVehicleLoss,
  getInsuranceCompanies,
  digitalInteractions,
  createClaimDigitalExit,
  createMissionDigitalExit,
  createMission,
  getAllMissions,
  cancelMission,
  cancelMissionCost,
  automaticCancelMission,
  putVehicleDestination,
  getCarDestinations,
  getSpecificCarDestinations,
  getTowLimits,
  getRosLimits,
  dispatchMission,
  updateMission,
  updateMissionType,
  startMissionMonitoring,
  createRentalMission,
  updateRentalMission,
  extendRentalMission,
  rentalCurrentLimits,
  rentalExtensionInfo,
  getCscSettings,
  transferToOem,
  createOemMission,
  createOemRentalMission,
};

const error = method => () => {
  throw new Error(`Unkown method: ${method}`);
};

const main = ({ method, ...rest }) => (methods[method] || error(method))(rest);
compose(
  forEach(([name, fn]) => (main[name] = fn)),
  toPairs
)(methods);
main.endpoints = compose(
  fromPairs,
  map(([k]) => [k, k]),
  toPairs
)(methods);

export default main;
